import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Library, Plus, Trash2, FileText, Download, Eye, Calendar, Building, X, Save, Upload, Search, Edit } from "lucide-react";

const emptyDoc = {
    doc_number: "", title: "", summary: "",
    category: "Quy phạm pháp luật", field: "Dân số - KHHGĐ", agency: "Bộ Y tế",
    signer: "", status: "Còn hiệu lực", issued_at: "",
    file_url: "", file_name: "", file_size: 0,
};

export default function AdminDocuments() {
    const [docs, setDocs] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState({ category: "", field: "", agency: "" });
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyDoc);
    const [editingId, setEditingId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const load = async () => {
        const params = new URLSearchParams();
        if (search) params.set("q", search);
        if (filter.category) params.set("category", filter.category);
        if (filter.field) params.set("field", filter.field);
        if (filter.agency) params.set("agency", filter.agency);
        const { data } = await api.get(`/documents?${params}`);
        setDocs(data.documents || []);
        setLoading(false);
    };

    useEffect(() => {
        api.get("/documents-meta/options").then((r) => setMeta(r.data));
        load();
    }, []); // eslint-disable-line

    useEffect(() => {
        const t = setTimeout(load, 350);
        return () => clearTimeout(t);
    }, [search, filter]); // eslint-disable-line

    const openCreate = () => { setEditingId(null); setForm(emptyDoc); setShowForm(true); setErr(""); setMsg(""); };
    const openEdit = (d) => {
        setEditingId(d.id);
        setForm({ ...emptyDoc, ...d, issued_at: d.issued_at?.slice(0, 10) || "" });
        setShowForm(true); setErr(""); setMsg("");
    };

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true); setErr("");
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const API = process.env.REACT_APP_BACKEND_URL;
            setForm({ ...form, file_url: API + data.url, file_name: data.filename, file_size: data.size });
            setMsg("Đã tải lên file thành công.");
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!form.title) { setErr("Tiêu đề là bắt buộc"); return; }
        setSaving(true); setErr("");
        try {
            const payload = { ...form, issued_at: form.issued_at ? new Date(form.issued_at).toISOString() : null };
            if (editingId) await api.put(`/admin/documents/${editingId}`, payload);
            else await api.post("/admin/documents", payload);
            setMsg(editingId ? "Đã cập nhật tài liệu." : "Đã tạo tài liệu mới.");
            setShowForm(false);
            await load();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Xác nhận xoá tài liệu này?")) return;
        try {
            await api.delete(`/admin/documents/${id}`);
            setDocs(docs.filter((d) => d.id !== id));
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="p-6 lg:p-10">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-bold flex items-center gap-2"><Library className="w-3 h-3" /> Quản lý nội dung</div>
                    <h1 className="font-heading text-3xl font-bold mt-1">Thư viện tài liệu</h1>
                    <p className="text-slate-600 mt-1">Quản lý văn bản pháp luật, tài liệu truyền thông, báo cáo.</p>
                </div>
                <button onClick={openCreate} className="btn-gold" data-testid="add-doc-btn"><Plus className="w-4 h-4" /> Thêm tài liệu</button>
            </div>

            {msg && <div className="text-emerald-700 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">{msg}</div>}
            {err && <div className="text-[var(--red)] text-sm bg-red-50 border border-red-200 rounded-lg p-3 mb-4">{err}</div>}

            {/* Filters */}
            <div className="card p-5 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="relative md:col-span-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input className="input pl-10" placeholder="Tìm theo tiêu đề hoặc số ký hiệu…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="doc-search" />
                </div>
                <select className="input" value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} data-testid="filter-category">
                    <option value="">Tất cả thể loại</option>
                    {meta?.categories?.map((c) => <option key={c}>{c}</option>)}
                </select>
                <select className="input" value={filter.field} onChange={(e) => setFilter({ ...filter, field: e.target.value })} data-testid="filter-field">
                    <option value="">Tất cả lĩnh vực</option>
                    {meta?.fields?.map((f) => <option key={f}>{f}</option>)}
                </select>
            </div>

            {/* List */}
            <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[var(--bg-alt)] text-xs uppercase tracking-widest text-slate-600">
                            <tr>
                                <th className="text-left p-4">Số ký hiệu</th>
                                <th className="text-left p-4">Trích yếu</th>
                                <th className="text-left p-4">Lĩnh vực</th>
                                <th className="text-left p-4">Cơ quan</th>
                                <th className="text-center p-4">Ngày BH</th>
                                <th className="text-center p-4">Tình trạng</th>
                                <th className="text-right p-4">Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? <tr><td colSpan={7} className="p-10 text-center text-slate-500">Đang tải…</td></tr>
                                : docs.length === 0 ? <tr><td colSpan={7} className="p-10 text-center text-slate-500">Chưa có tài liệu phù hợp.</td></tr>
                                : docs.map((d) => (
                                    <tr key={d.id} className="border-t border-[var(--border)] hover:bg-[var(--bg-alt)]" data-testid={`doc-row-${d.id}`}>
                                        <td className="p-4 font-mono text-xs text-[var(--navy)]">{d.doc_number || "—"}</td>
                                        <td className="p-4">
                                            <div className="font-semibold text-[var(--navy)]">{d.title}</div>
                                            {d.summary && <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">{d.summary}</div>}
                                        </td>
                                        <td className="p-4 text-xs"><span className="badge badge-slate">{d.field}</span></td>
                                        <td className="p-4 text-xs text-slate-600">{d.agency}</td>
                                        <td className="p-4 text-xs text-center whitespace-nowrap">{d.issued_at ? new Date(d.issued_at).toLocaleDateString("vi-VN") : "—"}</td>
                                        <td className="p-4 text-center">
                                            <span className={`badge ${d.status === "Còn hiệu lực" ? "badge-success" : "badge-slate"}`}>{d.status}</span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-1">
                                                {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer" className="btn-ghost !p-1.5" title="Tải"><Download className="w-4 h-4" /></a>}
                                                <button onClick={() => openEdit(d)} className="btn-ghost !p-1.5" data-testid={`edit-doc-${d.id}`}><Edit className="w-4 h-4" /></button>
                                                <button onClick={() => remove(d.id)} className="btn-ghost !p-1.5 !text-[var(--red)]" data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 bg-[var(--navy)]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <div className="card bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-5">
                            <h2 className="font-heading text-xl font-bold">{editingId ? "Chỉnh sửa tài liệu" : "Thêm tài liệu mới"}</h2>
                            <button onClick={() => setShowForm(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <DocField label="Số ký hiệu" testId="doc-number-input">
                                <input className="input" value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })} placeholder="VD: 04/2025/UBTVQH" />
                            </DocField>
                            <DocField label="Ngày ban hành" testId="doc-issued-input">
                                <input type="date" className="input" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} />
                            </DocField>
                            <DocField label="Tiêu đề / Trích yếu *" full testId="doc-title-input">
                                <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="VD: Pháp lệnh sửa đổi…" />
                            </DocField>
                            <DocField label="Mô tả ngắn" full testId="doc-summary-input">
                                <textarea className="input" rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
                            </DocField>
                            <DocField label="Thể loại" testId="doc-category-select">
                                <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                    {meta?.categories?.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </DocField>
                            <DocField label="Lĩnh vực" testId="doc-field-select">
                                <select className="input" value={form.field} onChange={(e) => setForm({ ...form, field: e.target.value })}>
                                    {meta?.fields?.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </DocField>
                            <DocField label="Cơ quan ban hành" testId="doc-agency-select">
                                <select className="input" value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })}>
                                    {meta?.agencies?.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </DocField>
                            <DocField label="Người ký">
                                <input className="input" value={form.signer} onChange={(e) => setForm({ ...form, signer: e.target.value })} />
                            </DocField>
                            <DocField label="Tình trạng" testId="doc-status-select">
                                <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                    {meta?.statuses?.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </DocField>
                            <DocField label="File đính kèm (PDF/DOC)" full testId="doc-file-input">
                                <div className="flex items-center gap-2">
                                    <label className="btn-secondary cursor-pointer">
                                        <Upload className="w-4 h-4" />{uploading ? "Đang tải…" : "Chọn file"}
                                        <input type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={handleFile} data-testid="file-upload-input" />
                                    </label>
                                    {form.file_name && (
                                        <span className="text-xs text-slate-600 truncate flex items-center gap-1">
                                            <FileText className="w-3 h-3" /> {form.file_name} ({(form.file_size / 1024).toFixed(0)} KB)
                                        </span>
                                    )}
                                </div>
                            </DocField>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setShowForm(false)} className="btn-secondary">Hủy</button>
                            <button onClick={submit} disabled={saving} className="btn-gold" data-testid="save-doc-btn">
                                <Save className="w-4 h-4" /> {saving ? "Đang lưu…" : (editingId ? "Lưu thay đổi" : "Tạo tài liệu")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function DocField({ label, full, children, testId }) {
    return (
        <div className={full ? "sm:col-span-2" : ""} data-testid={testId}>
            <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">{label}</label>
            {children}
        </div>
    );
}
